import { ErrorHandler, Injectable } from '@angular/core';
import isNil from 'lodash/isNil';

import { FrontendLoggerService } from './frontend-logger.service';

/**
 * App wide error handler responsible for calling the logger service to log errors back to the server.
 *
 * @author Jesse Bloomster e020902
 */
@Injectable()
export class AppErrorHandler implements ErrorHandler {
	/**
	 * @param frontendLoggerService the global frontend logger service
	 */
	constructor(private frontendLoggerService: FrontendLoggerService) {
		this.setupWindowOnErrorCallback();
	}

	/**
	 * Application-level error handler for managing all errors captured within Angular's context. Specifically, we log
	 * all general errors.
	 *
	 * @param error the captured error
	 */
	handleError(error: any) {
		this.frontendLoggerService.error(error);
	}

	/**
	 * Set up a callback method on the {@link window.onerror} handler to intercept and log any global errors that get
	 * thrown outside of Angular's context.
	 */
	private setupWindowOnErrorCallback() {
		window.onerror = (
			message: string | Event,
			_source?: string,
			_lineno?: number,
			_colno?: number,
			error?: Error
		): void => {
			// if we have an error object, then just pass it to the handler's handleError() method
			if (!isNil(error)) {
				this.handleError(error);
			} else if (message instanceof ErrorEvent && !isNil(message.error)) {
				this.handleError(message.error);
			} else {
				// if we don't have an error object, then just log the message
				this.frontendLoggerService.error(new Error(`Error Message: ${message}`));
			}
		};
	}
}
